import { ChangeDetectionStrategy, Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { LastSearchService } from "../../services/internal-services/lastsearch.service";
import { ApiViewerService } from "../../services/internal-services/api-viewer.service";
import { SearchManagerComponent } from "./search-manager.component";
import { SearchedInfo } from "../../model/searchedinfo";
import { IMetadataImage } from "../../interfaces/i-metadataimage";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { SearchCommunicatorService } from "../../services/communicators/search-communicator.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { MapService } from "../../services/internal-services/map.service";
import { take } from "rxjs/operators";
import { GeoService } from "../../services/internal-services/geo.service";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-phoenix-search-manager",
  templateUrl: "./search-manager.component.html",
  styleUrls: ["./search-manager.component.scss"],
  standalone: false,
})
export class PhoenixSearchManagerComponent extends SearchManagerComponent {
  constructor(
    mapService: MapService,
    lastSearchService: LastSearchService,
    apiViewerService: ApiViewerService,
    public searchCommunicatorService: SearchCommunicatorService,
    protected googleAnalyticsService: GoogleAnalyticsService,
    public dialogGallery: MatDialog,
  ) {
    super(mapService, lastSearchService, apiViewerService, dialogGallery);
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.subscriptions.push(
      this.viewerService.image$.subscribe((image: IMetadataImage) => {
        this.selectedImage = image;
        if (!this.selectedImage && this.previousSearch) {
          this.handleDiscreteAfterSearchWithoutResults();
        }
      }),
    );
    this.subscriptions.push(
      this.searchCommunicatorService.searchLocationRequest$.subscribe((location) => {
        if (location instanceof SearchedInfo) {
          this.handleSearchInfo(location);
        } else {
          this.searchAddress(location);
        }
        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.LOCATION_SEARCH_BAR,
          GoogleAnalyticsConstantsCategory.HEADER_BAR,
          true,
        );
      }),
    );
  }

  /**
   * Handle search info after reverse geocode
   * If discrete image is enabled, save data and wait the new image metadata
   * If ortho mode is enabled, get visible layers, images for this layer, and after that
   * get new layer with coverage on top of others and get the image
   * @param searchedInfo
   */
  handleSearchInfo(searchedInfo: SearchedInfo): void {
    this.previousImage = null;
    this.previousSearch = null;

    const previousLocation = this.viewerService.getCenterCoordinates();
    const newLocation = [searchedInfo.location.lng(), searchedInfo.location.lat()];
    const sameLocation = GeoService.isCenterEquals(previousLocation, newLocation);

    this.viewerService.setZoom(ViewerConstants.DEFAULT_SEARCH_ZOOM);
    this.viewerService.setCenterCoordinates(newLocation);

    this.mapService.setCenter(newLocation, ViewerConstants.PROJECTIONS.WGS84);
    if (!sameLocation) {
      if (this.mapService.swipeStatus.enable) {
        this.viewerService.searchEnd({ search: true });
      }
      this.viewerService.projectStatusTrigger$.pipe(take(1)).subscribe(() => {
        this.processSearchInfo(searchedInfo);
      });
    } else {
      this.processSearchInfo(searchedInfo);
    }
  }

  processSearchInfo(searchedInfo: SearchedInfo) {
    // this.viewerService.showNewestProject(); //newest is always on top
    if (this.viewerService.is3DMode()) {
      // subscribe to the next image loaded and after that get data for the image
      this.handleDiscreteSearch(searchedInfo);
    } else {
      this.handleOrthoSearch(searchedInfo);
    }

    this.showLocationInfo(searchedInfo);
    this.searchCommunicatorService.setSearchLocationResponse(searchedInfo);
  }

  handleDiscreteSearch(searchedInfo: SearchedInfo): void {
    this.setAddressResultToMap(searchedInfo);
    this.previousImage = this.selectedImage;
    this.previousSearch = searchedInfo;
  }

  handleDiscreteAfterSearchWithoutResults() {
    this.handleOrthoSearch(this.previousSearch);
  }

  handleOrthoSearch(searchedInfo: SearchedInfo): void {
    this.setAddressResultToMap(searchedInfo);
  }

  setAddressResultToMap(searchedInfo: SearchedInfo): void {
    this.lastSearchService.addLastSearch(searchedInfo);
  }
}
